import { ReactNode, useCallback, useEffect, useMemo } from "react";
import {
  FormattedDate,
  FormattedList,
  FormattedMessage,
  FormattedNumber,
  defineMessages,
  useIntl,
  type MessageDescriptor,
} from "react-intl";
import { Control, useController } from "react-hook-form";

import { Checkbox, Label } from "@fluentui/react-components";

import type { AdditionalDetails } from "@aglocal/schema/PostMeetingForm";
import type { TalentMeetingDetails } from "@aglocal/schema/TalentMeetingDetails";

import Stack from "@aglocal/web/components/Stack";

import { UseFormSetDefault } from "@/hooks/useSetDefault";

import { PostMeetingFormWithSummary } from "../form";

const SECURITY_CLEARANCE_LABELS = defineMessages({
  "Confidential Or R": {
    defaultMessage: "Confidential Or R",
    description: "Security clearance level held by the talent.",
  },
  "Secret Or L": {
    defaultMessage: "Secret Or L",
    description: "Security clearance level held by the talent.",
  },
  "Top Secret Or Q": {
    defaultMessage: "Top Secret Or Q",
    description: "Security clearance level held by the talent.",
  },
  No: {
    defaultMessage: "No",
    description: "Security clearance level held by the talent: none.",
  },
});

const FED_CLEARANCE_LABELS = defineMessages({
  CIA: {
    defaultMessage: "CIA",
    description:
      "Federal agency that granted the talent's clearance; an acronym that stays untranslated.",
  },
  DHS: {
    defaultMessage: "DHS",
    description:
      "Federal agency that granted the talent's clearance; an acronym that stays untranslated.",
  },
  DOD: {
    defaultMessage: "DOD",
    description:
      "Federal agency that granted the talent's clearance; an acronym that stays untranslated.",
  },
  DOE: {
    defaultMessage: "DOE",
    description:
      "Federal agency that granted the talent's clearance; an acronym that stays untranslated.",
  },
  FBI: {
    defaultMessage: "FBI",
    description:
      "Federal agency that granted the talent's clearance; an acronym that stays untranslated.",
  },
  "Military Branch": {
    defaultMessage: "Military Branch",
    description: "Kind of body that granted the talent's federal clearance.",
  },
  Other: {
    defaultMessage: "Other",
    description: "Kind of body that granted the talent's federal clearance.",
  },
});

const PLACEMENT_TYPE_LABELS = defineMessages({
  any: {
    defaultMessage: "Any",
    description: "Kind of placement the talent is looking for.",
  },
  contract: {
    defaultMessage: "Contract",
    description: "Kind of placement the talent is looking for.",
  },
  "contract-to-hire": {
    defaultMessage: "Contract to hire",
    description: "Kind of placement the talent is looking for.",
  },
  "direct-placement": {
    defaultMessage: "Direct placement",
    description: "Kind of placement the talent is looking for.",
  },
});

const SCHEDULE_LABELS = defineMessages({
  "full-time": {
    defaultMessage: "Full-time",
    description: "Kind of schedule the talent is looking for.",
  },
  "part-time": {
    defaultMessage: "Part-time",
    description: "Kind of schedule the talent is looking for.",
  },
  both: {
    defaultMessage: "Both",
    description:
      "Kind of schedule the talent is looking for: both full-time and part-time.",
  },
});

const SHIFT_LABELS = defineMessages({
  First: {
    defaultMessage: "First",
    description: "Work shift the talent is looking for.",
  },
  Second: {
    defaultMessage: "Second",
    description: "Work shift the talent is looking for.",
  },
  Third: {
    defaultMessage: "Third",
    description: "Work shift the talent is looking for.",
  },
  Weekend: {
    defaultMessage: "Weekend",
    description: "Work shift the talent is looking for.",
  },
});

const PAY_FREQUENCY_LABELS = defineMessages({
  Hourly: {
    defaultMessage: "hourly",
    description: "How often the talent's desired pay rate is paid.",
  },
  Daily: {
    defaultMessage: "daily",
    description: "How often the talent's desired pay rate is paid.",
  },
  Weekly: {
    defaultMessage: "weekly",
    description: "How often the talent's desired pay rate is paid.",
  },
  Monthly: {
    defaultMessage: "monthly",
    description: "How often the talent's desired pay rate is paid.",
  },
});

const COMMUTE_UNIT_LABELS = defineMessages({
  minutes: {
    defaultMessage: "minutes",
    description: "Unit of the talent's commute preference.",
  },
  miles: {
    defaultMessage: "miles",
    description: "Unit of the talent's commute preference.",
  },
  kilometers: {
    defaultMessage: "kilometers",
    description: "Unit of the talent's commute preference.",
  },
});

const messages = defineMessages({
  heading: {
    defaultMessage: "Additional details found",
    description:
      "Label above the talent details detected in the conversation that the recruiter can include in the Connected update.",
  },
  securityClearance: {
    defaultMessage: "Security clearance of <strong>{value}</strong>.",
    description: "Detected talent detail; {value} is the clearance level.",
  },
  fedClearanceType: {
    defaultMessage: "Federal clearance type of <strong>{value}</strong>.",
    description: "Detected talent detail; {value} is the clearance type.",
  },
  dateAvailable: {
    defaultMessage: "Date available of <strong>{date}</strong>.",
    description:
      "Detected talent detail; {date} is the formatted date the talent can start.",
  },
  placementType: {
    defaultMessage: "Placement type of <strong>{types}</strong>.",
    description:
      "Detected talent detail; {types} is a list of placement types joined with 'or'.",
  },
  schedule: {
    defaultMessage: "Desired schedule of <strong>{value}</strong>.",
    description: "Detected talent detail; {value} is the schedule type.",
  },
  shift: {
    defaultMessage: "Desired shift of <strong>{shifts}</strong>.",
    description:
      "Detected talent detail; {shifts} is a list of shifts joined with 'or'.",
  },
  payRate: {
    defaultMessage:
      "Compensation pay rate of <strong>{range} {frequency}</strong>.",
    description:
      "Detected talent detail; {range} is a formatted amount or range and {frequency} how often it is paid (e.g. hourly).",
  },
  salary: {
    defaultMessage:
      "Compensation salary of <strong>{range} {currency}</strong>.",
    description:
      "Detected talent detail; {range} is a formatted amount or range and {currency} the currency code.",
  },
  willingToRelocate: {
    defaultMessage:
      "{answer, select, Yes {<strong>Willing</strong> to relocate.} No {<strong>Not willing</strong> to relocate.} other {<strong>Unknown willing</strong> to relocate.}}",
    description:
      "Detected talent detail; {answer} is Yes, No or Unknown and selects the sentence.",
  },
  nationalOpportunities: {
    defaultMessage:
      "{answer, select, Yes {<strong>Open to</strong> national opportunities.} No {<strong>Not open to</strong> national opportunities.} other {<strong>Unknown preference for</strong> national opportunities.}}",
    description:
      "Detected talent detail; {answer} is Yes, No or Unknown and selects the sentence.",
  },
  reliableTransportation: {
    defaultMessage:
      "{answer, select, Yes {<strong>Has</strong> reliable transportation.} No {<strong>Does not have</strong> reliable transportation.} other {<strong>Unknown</strong> reliable transportation.}}",
    description:
      "Detected talent detail; {answer} is Yes, No or Unknown and selects the sentence.",
  },
  commutePreference: {
    defaultMessage: "Commute preference of <strong>{value} {unit}</strong>.",
    description:
      "Detected talent detail; {value} is a formatted number and {unit} its unit (e.g. miles).",
  },
  range: {
    defaultMessage: "{low} - {high}",
    description:
      "Range between two amounts; {low} and {high} are formatted currency values.",
  },
});

interface CurrencyRangeProps {
  low: number;
  high?: number | null;
  currency?: string;
  maximumFractionDigits?: number;
}

function CurrencyRange({
  low,
  high,
  currency,
  maximumFractionDigits,
}: CurrencyRangeProps) {
  const format = (value: number) => (
    <FormattedNumber
      style={currency ? "currency" : "decimal"}
      value={value}
      currency={currency}
      maximumFractionDigits={maximumFractionDigits}
    />
  );
  if (high == null) {
    return format(low);
  }
  return (
    <FormattedMessage
      {...messages.range}
      values={{ low: format(low), high: format(high) }}
    />
  );
}

interface DetailOption {
  name: keyof AdditionalDetails;
  label: ReactNode;
  value: unknown;
}

export interface DetailsFieldProps {
  details: Readonly<TalentMeetingDetails> | undefined;
  control: Control<PostMeetingFormWithSummary>;
  setDefault: UseFormSetDefault<PostMeetingFormWithSummary>;
}

export default function DetailsField({
  details,
  control,
  setDefault,
}: DetailsFieldProps) {
  const { $t } = useIntl();
  /** Localized label for a raw schema value, falling back to the value itself. */
  const label = useCallback(
    (labels: Record<string, MessageDescriptor>, value: string) =>
      Object.hasOwn(labels, value) ? $t(labels[value]) : value,
    [$t],
  );
  const {
    field,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useController({
    control,
    name: "additionalDetails",
  });

  const options = useMemo<DetailOption[]>(() => {
    const result: DetailOption[] = [];

    if (!details) {
      return result;
    }

    const { workPreferences, compensationPreferences, locationPreferences } =
      details;

    if (details.securityClearance) {
      result.push({
        name: "securityClearance",
        label: (
          <FormattedMessage
            {...messages.securityClearance}
            values={{
              value: label(
                SECURITY_CLEARANCE_LABELS,
                details.securityClearance,
              ),
            }}
          />
        ),
        value: details.securityClearance,
      });
    }

    if (details.fedClearanceType) {
      result.push({
        name: "fedClearanceType",
        label: (
          <FormattedMessage
            {...messages.fedClearanceType}
            values={{
              value: label(FED_CLEARANCE_LABELS, details.fedClearanceType),
            }}
          />
        ),
        value: details.fedClearanceType,
      });
    }

    if (workPreferences?.dateAvailable) {
      result.push({
        name: "dateAvailable",
        label: (
          <FormattedMessage
            {...messages.dateAvailable}
            values={{
              date: (
                <FormattedDate
                  value={workPreferences.dateAvailable}
                  timeZone="UTC"
                />
              ),
            }}
          />
        ),
        value: workPreferences.dateAvailable,
      });
    }

    if (workPreferences?.placementType?.length) {
      result.push({
        name: "desiredPlacementType",
        label: (
          <FormattedMessage
            {...messages.placementType}
            values={{
              types: (
                <FormattedList
                  type="disjunction"
                  value={workPreferences.placementType.map((type) =>
                    label(PLACEMENT_TYPE_LABELS, type),
                  )}
                />
              ),
            }}
          />
        ),
        value: workPreferences.placementType,
      });
    }

    if (workPreferences?.schedule) {
      result.push({
        name: "desiredSchedule",
        label: (
          <FormattedMessage
            {...messages.schedule}
            values={{ value: label(SCHEDULE_LABELS, workPreferences.schedule) }}
          />
        ),
        value: workPreferences.schedule,
      });
    }

    if (workPreferences?.shift?.length) {
      result.push({
        name: "desiredShift",
        label: (
          <FormattedMessage
            {...messages.shift}
            values={{
              shifts: (
                <FormattedList
                  type="disjunction"
                  value={workPreferences.shift.map((shift) =>
                    label(SHIFT_LABELS, shift),
                  )}
                />
              ),
            }}
          />
        ),
        value: workPreferences.shift,
      });
    }

    if (compensationPreferences?.payRate) {
      const { payRate } = compensationPreferences;
      result.push({
        name: "desiredPayRate",
        label: (
          <FormattedMessage
            {...messages.payRate}
            values={{
              range: <CurrencyRange low={payRate.low} high={payRate.high} />,
              frequency: label(PAY_FREQUENCY_LABELS, payRate.frequency),
            }}
          />
        ),
        value: payRate,
      });
    }

    if (compensationPreferences?.salary) {
      const { salary } = compensationPreferences;
      const currency = salary.currency.split(" - ")[0];
      result.push({
        name: "desiredSalary",
        label: (
          <FormattedMessage
            {...messages.salary}
            values={{
              range: (
                <CurrencyRange
                  low={salary.low}
                  high={salary.high}
                  currency={currency}
                  maximumFractionDigits={0}
                />
              ),
              currency,
            }}
          />
        ),
        value: salary,
      });
    }

    if (locationPreferences?.willingToRelocate) {
      result.push({
        name: "willingToRelocate",
        label: (
          <FormattedMessage
            {...messages.willingToRelocate}
            values={{ answer: locationPreferences.willingToRelocate }}
          />
        ),
        value: locationPreferences.willingToRelocate,
      });
    }

    if (locationPreferences?.nationalOpportunities) {
      result.push({
        name: "nationalOpportunities",
        label: (
          <FormattedMessage
            {...messages.nationalOpportunities}
            values={{ answer: locationPreferences.nationalOpportunities }}
          />
        ),
        value: locationPreferences.nationalOpportunities,
      });
    }

    if (locationPreferences?.reliableTransportation) {
      result.push({
        name: "reliableTransportation",
        label: (
          <FormattedMessage
            {...messages.reliableTransportation}
            values={{ answer: locationPreferences.reliableTransportation }}
          />
        ),
        value: locationPreferences.reliableTransportation,
      });
    }

    if (locationPreferences?.commutePreference) {
      const { commutePreference } = locationPreferences;
      result.push({
        name: "commutePreference",
        label: (
          <FormattedMessage
            {...messages.commutePreference}
            values={{
              value: <FormattedNumber value={commutePreference.value} />,
              unit: label(COMMUTE_UNIT_LABELS, commutePreference.unit),
            }}
          />
        ),
        value: commutePreference,
      });
    }

    return result;
  }, [details, label]);

  useEffect(() => {
    setDefault(
      "additionalDetails",
      Object.fromEntries(options.map(({ name, value }) => [name, value])),
    );
  }, [setDefault, options]);

  if (options.length === 0) {
    return null;
  }

  return (
    <Stack gap="none">
      <Label>{$t(messages.heading)}</Label>
      {options.map(({ name, label, value }) => (
        <Checkbox
          key={name}
          label={{
            children: label,
          }}
          checked={field.value[name] != null}
          disabled={isSubmitting || isSubmitSuccessful}
          onChange={(event) => {
            field.onChange({
              ...field.value,
              [name]: event.target.checked ? value : undefined,
            });
          }}
        />
      ))}
    </Stack>
  );
}
